import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Package, Clock, TrendingUp, PieChart, CalendarCheck, ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { fetchBuildTimeSummary } from '@/lib/api/buildTimeStats';
import { BuildTimeSummaryValue } from '@/components/BuildDuration';
import { creditedUnits } from '@/lib/teamwork';


const SKU_FAMILY: Record<string, string> = {
  Z: 'Zero', '1': 'One', P: 'Pro', M: 'Master', L: 'Legend', E: 'Elite', C: 'Custom',
};
const getFamilyFromSku = (sku: string): string => {
  if (!sku) return 'Unknown';
  const prefix = sku.startsWith('AOI-') ? sku[4] : sku[0];
  return SKU_FAMILY[prefix] ?? 'Unknown';
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  picking: 'bg-orange-100 text-orange-800',
  building: 'bg-blue-100 text-blue-800',
  testing: 'bg-purple-100 text-purple-800',
  awaiting_review: 'bg-pink-100 text-pink-800',
  in_progress: 'bg-purple-100 text-purple-800',
};

const COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-purple-500',
  'bg-orange-500', 'bg-pink-500', 'bg-cyan-500',
];

export function TechnicianDashboardPage() {
  const [dateRange, setDateRange] = useState({
    from: '2022-01-01',
    to: new Date().toISOString().split('T')[0],
  });

  // Load the current technician's own record
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['my-technician-record'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data, error } = await supabase
        .from('technicians')
        .select('id, name, initials')
        .eq('user_id', user.id)
        .single();
      if (error) { console.error('Error fetching technician:', error); return null; }
      return data as { id: string; name: string; initials: string };
    },
  });

  // PAO stats - units this technician built (unit_builders), which on a team
  // order can differ from the orders assigned to them
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['my-pao-stats', me?.id, dateRange],
    queryFn: async () => {
      if (!me?.id) return { totalUnits: 0, unitsThisWeek: 0, familyData: [] };

      const { data: paoData, error } = await supabase
        .from('assembly_orders')
        .select('id, build_sku, completed_date, quantity, technician_id, unit_builders')
        .contains('builder_ids', [me.id])
        .gte('completed_date', dateRange.from)
        .lte('completed_date', dateRange.to);

      if (error) { console.error('PAO query error:', error); return { totalUnits: 0, unitsThisWeek: 0, familyData: [] }; }
      if (!paoData || paoData.length === 0) return { totalUnits: 0, unitsThisWeek: 0, familyData: [] };

      const mine = (p: any) => creditedUnits(p, [me.id]);
      const totalUnits = paoData.reduce((sum, p) => sum + mine(p), 0);

      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const unitsThisWeek = paoData
        .filter(p => new Date(p.completed_date) >= oneWeekAgo)
        .reduce((sum, p) => sum + mine(p), 0);

      const familyBreakdown: Record<string, number> = {};
      paoData.forEach((p: any) => {
        const qty = mine(p);
        if (!qty) return;
        const family = getFamilyFromSku(p.build_sku || '');
        familyBreakdown[family] = (familyBreakdown[family] || 0) + qty;
      });
      const familyData = Object.entries(familyBreakdown)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);

      return { totalUnits, unitsThisWeek, familyData };
    },
    enabled: !!me?.id,
  });

  // Typical build time: median, measured as on each order's detail page
  const { data: buildTime } = useQuery({
    queryKey: ['my-build-time', me?.id, dateRange],
    queryFn: () => fetchBuildTimeSummary(q => q.contains('builder_ids', [me!.id]), dateRange.from, dateRange.to),
    enabled: !!me?.id,
  });

  // My Orders — active assembly orders assigned to this technician
  const { data: myOrders, isLoading: ordersLoading } = useQuery({
    queryKey: ['my-active-orders', me?.id],
    queryFn: async () => {
      if (!me?.id) return { count: 0, items: [] };
      const { data, error, count } = await supabase
        .from('assembly_orders')
        .select('id, order_number, build_sku, quantity, status, created_at', { count: 'exact' })
        .eq('technician_id', me.id)
        .in('status', ['pending', 'picking', 'building', 'testing', 'awaiting_review', 'in_progress'])
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) { console.error('My orders error:', error); return { count: 0, items: [] }; }
      return { count: count || 0, items: data || [] };
    },
    enabled: !!me?.id,
  });

  if (meLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">My Dashboard</h1>
        <p className="text-muted-foreground">
          {me ? `${me.name} (${me.initials}) · build performance overview` : 'Your build performance overview'}
        </p>
      </div>

      {/* Date filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">From:</label>
              <input
                type="date"
                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={dateRange.from}
                onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">To:</label>
              <input
                type="date"
                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={dateRange.to}
                onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Units Built</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statsLoading ? '...' : stats?.totalUnits ?? 0}</div>
            <p className="text-xs text-muted-foreground">In selected period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Units Built This Week</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statsLoading ? '...' : stats?.unitsThisWeek ?? 0}</div>
            <p className="text-xs text-muted-foreground">Last 7 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">My Orders</CardTitle>
            <CalendarCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{ordersLoading ? '...' : myOrders?.count ?? 0}</div>
            <p className="text-xs text-muted-foreground">Active assembly orders</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Typical Build Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <BuildTimeSummaryValue summary={buildTime} />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* My Orders List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5" />
              My Orders
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/my-orders">View All <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </CardHeader>
          <CardContent>
            {ordersLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : myOrders?.items && myOrders.items.length > 0 ? (
              <div className="space-y-3">
                {myOrders.items.map((order: any) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate font-mono">{order.build_sku || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground truncate">{order.order_number}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <span className="text-sm font-medium">×{order.quantity || 1}</span>
                      <Badge className={cn('text-xs', STATUS_COLORS[order.status] || 'bg-gray-100')}>
                        {order.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No active orders</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Build Family Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Build Family Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : stats?.familyData && stats.familyData.length > 0 ? (
              <div className="space-y-3">
                {stats.familyData.map((item, index) => {
                  const maxCount = Math.max(...stats.familyData.map(d => d.count));
                  const pct = (item.count / maxCount) * 100;
                  const totalPct = stats.totalUnits > 0 ? (item.count / stats.totalUnits) * 100 : 0;
                  return (
                    <div key={item.name} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium truncate">{item.name}</span>
                        <span className="text-muted-foreground ml-2">{item.count} ({totalPct.toFixed(0)}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${COLORS[index % COLORS.length]} rounded-full transition-all`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No data in selected period</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
